import React, { useState } from 'react';
import { Card, Table, Form, Input, Select, Button, Modal, message, InputNumber, Radio } from 'antd';
import { useTable } from '@/hooks/useTable';
import { getOrderList } from '@/api/pointsMall';
import { updateUserPoints } from '@/api/userPoints';
import type { OrderListParams } from '@/api/pointsMall';
import type { UpdateUserPointsParams } from '@/api/userPoints';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

const { Option } = Select;

const orderStatusOptions = [
  { label: '全部', value: '' },
  { label: '待发货', value: 0 },
  { label: '已发货', value: 1 },
  { label: '已完成', value: 2 },
  { label: '已取消', value: 3 },
];

const productTypeOptions = [
  { label: '全部', value: '' },
  { label: '壁纸', value: 1 },
  { label: '实物', value: 2 },
];

const PointsMallPage: React.FC = () => {
  const [form] = Form.useForm();
  const [pointsForm] = Form.useForm();
  const [searchParams, setSearchParams] = useState<Partial<OrderListParams>>({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const {
    data,
    total,
    currentPage,
    pageSize,
    isLoading,
    handleTableChange,
    updateParams,
  } = useTable({
    queryKey: 'points-mall-order-list',
    queryFn: async (current, size, params) => {
      const response = await getOrderList({
        currentPage: current,
        pageSize: size,
        ...params,
      });
      return response.data;
    },
    defaultParams: searchParams,
  });

  const handleSearch = async () => {
    const values = await form.validateFields();
    const params: Partial<OrderListParams> = {
      ...values,
      orderStatus: values.orderStatus || undefined,
      productType: values.productType || undefined,
    };
    setSearchParams(params);
    updateParams(params);
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({});
    updateParams({});
  };

  const handleUpdatePoints = async () => {
    try {
      const values = await pointsForm.validateFields();
      const params: UpdateUserPointsParams = {
        userId: selectedUserId,
        points: values.points,
        operatorType: values.operatorType,
      };
      
      const res = await updateUserPoints(params);
      if (res.data.status === 200) {
        message.success('积分更新成功');
        setIsModalVisible(false);
        pointsForm.resetFields();
        // Refresh the table data
        updateParams(searchParams);
      } else {
        message.error(res.data.message || '积分更新失败');
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const showPointsModal = (userId: string) => {
    setSelectedUserId(userId);
    setIsModalVisible(true);
  };

  const columns = [
    {
      title: '订单ID',
      dataIndex: 'orderId',
      width: 180,
    },
    {
      title: '用户ID',
      dataIndex: 'userId',
      width: 100,
    },
    {
      title: '用户名',
      dataIndex: 'userName',
      width: 120,
    },
    {
      title: '商品信息',
      dataIndex: 'productTitle',
      width: 200,
      render: (title: string, record: any) => (
        <div>
          <div>{title}</div>
          <img
            src={record.productShowImage}
            alt={title}
            style={{ width: 60, height: 60, marginTop: 8 }}
          />
        </div>
      ),
    },
    {
      title: '商品类型',
      dataIndex: 'productTypeDesc',
      width: 100,
    },
    {
      title: '数量',
      dataIndex: 'productCount',
      width: 80,
    },
    {
      title: '积分',
      dataIndex: 'points',
      width: 100,
    },
    {
      title: '总积分',
      dataIndex: 'sumPoints',
      width: 100,
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatus',
      width: 100,
      render: (status: number) => {
        const option = orderStatusOptions.find((opt) => opt.value === status);
        return option ? option.label : '-';
      },
    },
    {
      title: '下单时间',
      dataIndex: 'createTime',
      width: 160,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <div>
          <Link to={`/points-mall/detail/${record.orderId}`}>查看详情</Link>
          <Button type="link" onClick={() => showPointsModal(record.userId)}>积分发放/减扣</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="points-mall-page">
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Form form={form} layout="inline">
            <Form.Item name="orderId" label="订单ID">
              <Input placeholder="请输入订单ID" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="productTitle" label="商品名称">
              <Input placeholder="请输入商品名称" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="userId" label="用户ID">
              <Input placeholder="请输入用户ID" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="userName" label="用户名">
              <Input placeholder="请输入用户名" style={{ width: 200 }} />
            </Form.Item>
            <Form.Item name="orderStatus" label="订单状态">
              <Select
                style={{ width: 120 }}
                placeholder="请选择状态"
                options={orderStatusOptions}
              />
            </Form.Item>
            <Form.Item name="productType" label="商品类型">
              <Select
                style={{ width: 120 }}
                placeholder="请选择类型"
                options={productTypeOptions}
              />
            </Form.Item>
            <Form.Item>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={handleSearch}>
                  搜索
                </button>
                <button type="button" onClick={handleReset}>
                  重置
                </button>
              </div>
            </Form.Item>
          </Form>
          <Button type="primary" onClick={() => showPointsModal('')}>
            积分发放/减扣
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="orderId"
          scroll={{ x: 1700 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          loading={isLoading}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title="积分发放/减扣"
        open={isModalVisible}
        onOk={handleUpdatePoints}
        onCancel={() => {
          setIsModalVisible(false);
          pointsForm.resetFields();
        }}
      >
        <Form
          form={pointsForm}
          layout="vertical"
        >
          {selectedUserId === '' && (
            <Form.Item
              name="userId"
              label="用户ID"
              rules={[{ required: true, message: '请输入用户ID' }]}
            >
              <Input placeholder="请输入用户ID" />
            </Form.Item>
          )}
          <Form.Item
            name="operatorType"
            label="操作类型"
            rules={[{ required: true, message: '请选择操作类型' }]}
          >
            <Radio.Group>
              <Radio value={1}>发放积分</Radio>
              <Radio value={2}>减扣积分</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="points"
            label="积分值"
            rules={[{ required: true, message: '请输入积分值' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              placeholder="请输入积分值"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PointsMallPage;
